import React from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import '../../assets/styles/DonutChart.css';

/* Grafico de anillo.
 *
 * Adaptado del componente original de shadcn/Tailwind: aqui no hay Tailwind ni
 * TypeScript, asi que las clases utilitarias viven en DonutChart.css y el
 * resaltado al pasar el raton se hace con una clase (`is-active`) en vez de
 * estilos en linea, para no pelearse con el `transform` que gestiona
 * framer-motion.
 *
 * Cada tramo se dibuja como un circulo con `stroke-dasharray`: un trozo
 * pintado del largo que le toca y el resto vacio, y `stroke-dashoffset` lo
 * desplaza hasta donde acaba el tramo anterior. El SVG va girado -90 grados
 * para que la serie arranque a las 12 en punto.
 */

const DonutChart = React.forwardRef(
  (
    {
      data,
      totalValue: propTotalValue,
      size = 200,
      strokeWidth = 20,
      animationDuration = 1,
      animationDelayPerSegment = 0.05,
      highlightOnHover = true,
      centerContent,
      onSegmentHover,
      activeLabel,
      className = '',
      ...props
    },
    ref
  ) => {
    const [hoveredSegment, setHoveredSegment] = React.useState(null);
    // `activeLabel` permite resaltar un tramo desde fuera (la leyenda, por
    // ejemplo). Si no llega, manda el raton sobre el propio anillo.
    const highlightedLabel =
      activeLabel !== undefined ? activeLabel : hoveredSegment?.label;
    const reduced = useReducedMotion() === true;

    const internalTotalValue = React.useMemo(
      () => propTotalValue || data.reduce((sum, segment) => sum + segment.value, 0),
      [data, propTotalValue]
    );

    const radius = size / 2 - strokeWidth / 2;
    const circumference = 2 * Math.PI * radius;

    // El aviso al padre va por ref para que no importe si nos pasan una
    // funcion nueva en cada render: si dependieramos de ella, un padre que
    // guarde el tramo en su estado entraria en bucle de renders.
    const hoverCallback = React.useRef(onSegmentHover);
    hoverCallback.current = onSegmentHover;

    React.useEffect(() => {
      if (hoverCallback.current) hoverCallback.current(hoveredSegment);
    }, [hoveredSegment]);

    let cumulativePercentage = 0;

    return (
      <div
        ref={ref}
        className={`donut ${className}`.trim()}
        style={{ width: size, height: size }}
        onMouseLeave={() => setHoveredSegment(null)}
        {...props}
      >
        <svg
          className="donut__svg"
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
        >
          {/* Anillo de fondo: marca el hueco completo aunque falten datos. */}
          <circle
            className="donut__track"
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="transparent"
            strokeWidth={strokeWidth}
          />

          <AnimatePresence>
            {data.map((segment, index) => {
              if (segment.value === 0) return null;

              const percentage =
                internalTotalValue === 0 ? 0 : (segment.value / internalTotalValue) * 100;

              const strokeDasharray = `${(percentage / 100) * circumference} ${circumference}`;
              const strokeDashoffset = (cumulativePercentage / 100) * circumference;
              const isActive = highlightedLabel === segment.label;

              cumulativePercentage += percentage;

              return (
                <motion.circle
                  key={segment.label || index}
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="transparent"
                  stroke={segment.color}
                  strokeWidth={strokeWidth}
                  strokeDasharray={strokeDasharray}
                  strokeLinecap="round"
                  initial={reduced ? false : { opacity: 0, strokeDashoffset: circumference }}
                  animate={{ opacity: 1, strokeDashoffset: -strokeDashoffset }}
                  transition={
                    reduced
                      ? { duration: 0 }
                      : {
                          opacity: {
                            duration: 0.3,
                            delay: index * animationDelayPerSegment
                          },
                          strokeDashoffset: {
                            duration: animationDuration,
                            delay: index * animationDelayPerSegment,
                            ease: 'easeOut'
                          }
                        }
                  }
                  className={[
                    'donut__segment',
                    highlightOnHover ? 'donut__segment--interactive' : '',
                    isActive ? 'is-active' : ''
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  style={{ '--donut-segment-color': segment.color }}
                  onMouseEnter={() => setHoveredSegment(segment)}
                />
              );
            })}
          </AnimatePresence>
        </svg>

        {centerContent && (
          <div
            className="donut__center"
            style={{
              width: size - strokeWidth * 2.5,
              height: size - strokeWidth * 2.5
            }}
          >
            {centerContent}
          </div>
        )}
      </div>
    );
  }
);

DonutChart.displayName = 'DonutChart';

export default DonutChart;
export { DonutChart };
